/**
 * 今天 —— 默认首页，依据 design/01-today-tablet.png 与 design/09-today-mobile.png。
 *
 * 视觉层级：今日预约（最大）> 需要注意 > 今日应交付。
 * 妈妈开门第一件事是「今天谁来」，第二件是「有没有出事」。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  PackageCheck,
  Phone,
  PhoneCall,
  Plus,
} from 'lucide-react'
import { api, type Me, type TodayData, type Appointment } from '../lib/api'
import {
  APPT_STATUS_ZH,
  ARCH_ZH,
  CASE_TYPE_SHORT,
  date as fmtDate,
  rm,
  time as fmtTime,
  weekday,
} from '../lib/format'
import { Badge, Button, Card, EmptyState, Skeleton, apptStatusTone, useToast } from '../components/ui'
import { NewCaseDrawer } from '../features/NewCaseDrawer'
import { AppointmentDrawer } from '../features/AppointmentDrawer'

export function TodayPage({ me }: { me: Me }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  const [caseFor, setCaseFor] = useState<{ id: string; fullName: string } | null>(null)
  const [claiming, setClaiming] = useState<string | null>(null)
  const [editAppt, setEditAppt] = useState<Appointment | null>(null)

  const today = useQuery({
    queryKey: ['today'],
    queryFn: () => api.get<TodayData>('/api/today'),
    refetchInterval: 120_000,
  })

  const setApptStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) =>
      api.post(`/api/appointments/${v.id}/status`, { status: v.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['today'] })
      toast.show('已更新')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const setCaseStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) =>
      api.post(`/api/cases/${v.id}/status`, { status: v.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['today'] })
      toast.show('已标记为已交付')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  /**
   * 顾客到店 → 直接开单。
   *
   * 未建档的电话预约在这里补建档：接电话时只问了姓名电话，
   * 人到了眼前才有时间问剩下的。建完顺手把预约认领过去，
   * 这样这位顾客的到店记录不会断成两截。
   */
  const startCase = async (a: Appointment) => {
    if (a.customer) {
      setCaseFor({ id: a.customer.id, fullName: a.customer.fullName })
      return
    }
    setClaiming(a.id)
    try {
      const r = await api.post<{ customer: { id: string; fullName: string } }>('/api/customers', {
        fullName: a.displayName,
        // 电话是必填，没留电话就先占位，建档后在顾客页补
        phone: a.phoneE164 ?? '0000000000',
      })
      await api.patch(`/api/appointments/${a.id}`, { customerId: r.customer.id })
      void qc.invalidateQueries({ queryKey: ['today'] })
      setCaseFor({ id: r.customer.id, fullName: r.customer.fullName })
    } catch (e) {
      toast.show((e as Error).message, { tone: 'bad' })
    } finally {
      setClaiming(null)
    }
  }

  if (today.isLoading) return <TodaySkeleton />
  if (!today.data) return null

  const d = today.data
  const isOwner = me.user.role === 'OWNER'
  const noAttention =
    d.attention.overdueCases.count === 0 &&
    d.attention.readyForPickup.count === 0 &&
    d.attention.outstanding.count === 0 &&
    d.attention.manualContacts.count === 0

  return (
    <div className="mx-auto max-w-7xl">
      <h1 className="mb-5 text-[28px] font-bold">
        今天 · <span className="tnum">{fmtDate(d.date)}</span>{' '}
        <span className="text-ink-2">{weekday(d.date)}</span>
        {!d.isBusinessDay && (
          <span className="ml-3 align-middle">
            <Badge tone="neutral">今天通常休息</Badge>
          </span>
        )}
      </h1>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* ── 左：今日预约 ── */}
        <Card title={`今日预约 (${d.appointments.length})`} bodyClassName="p-0! sm:p-0!">
          {/* 空状态不提交付情况 —— 右侧「今日应交付」卡片已经说了，说两遍是噪音 */}
          {d.appointments.length === 0 ? (
            <EmptyState icon={<CalendarDays size={44} />} title="今天没有预约" />
          ) : (
            <ul>
              {d.appointments.map((a) => (
                <AppointmentRow
                  key={a.id}
                  appt={a}
                  busy={setApptStatus.isPending}
                  onArrived={() => setApptStatus.mutate({ id: a.id, status: 'ARRIVED' })}
                  onDone={() => setApptStatus.mutate({ id: a.id, status: 'DONE' })}
                  onOpen={() => a.case && navigate(`/cases/${a.case.id}`)}
                  onStartCase={() => startCase(a)}
                  claiming={claiming === a.id}
                  onEdit={() => setEditAppt(a)}
                />
              ))}
            </ul>
          )}
        </Card>

        {/* ── 右：需要注意 + 今日应交付 ── */}
        <div className="flex flex-col gap-4">
          <Card title="需要注意" bodyClassName="p-0! sm:p-0!">
            {noAttention ? (
              // 用 Lucide 图标而非 emoji，跟全站图标语言保持一致
              <div className="flex items-center justify-center gap-2 px-5 py-6 text-[16px] text-ok">
                <CheckCircle2 size={20} />
                没有需要处理的事情
              </div>
            ) : (
              <ul>
                <AttentionRow
                  icon={<AlertTriangle size={22} className="text-bad" />}
                  label="逾期未交付"
                  value={`${d.attention.overdueCases.count} 单`}
                  sub={
                    d.attention.overdueCases.count > 0
                      ? `最久逾期 ${d.attention.overdueCases.maxOverdueDays} 天`
                      : undefined
                  }
                  tone="bad"
                  onClick={() => navigate('/cases?filter=overdue')}
                />
                <AttentionRow
                  icon={<PackageCheck size={22} className="text-brand-600" />}
                  label="待取件"
                  value={`${d.attention.readyForPickup.count} 单`}
                  sub={
                    d.attention.readyForPickup.count > 0
                      ? `做好超过 ${d.attention.readyForPickup.maxWaitingDays} 天`
                      : undefined
                  }
                  onClick={() => navigate('/cases?filter=ready')}
                />
                <AttentionRow
                  icon={<CircleDollarSign size={22} className="text-warn" />}
                  label="待收尾款"
                  // 助手看到笔数，看不到金额
                  value={isOwner ? rm(d.attention.outstanding.totalAmount) : `${d.attention.outstanding.count} 单`}
                  sub={isOwner ? `共 ${d.attention.outstanding.count} 单` : undefined}
                  tone="warn"
                  onClick={() => isOwner && navigate('/finance')}
                />
                <AttentionRow
                  icon={<PhoneCall size={22} className="text-ink-2" />}
                  label="待通知顾客"
                  value={`${d.attention.manualContacts.count} 位`}
                  sub={
                    d.attention.manualContacts.count > 0 ? '点一下就能发' : undefined
                  }
                  onClick={() => navigate('/reminders')}
                />
              </ul>
            )}
          </Card>

          <Card title={`今日应交付 (${d.dueToday.length})`} bodyClassName="p-0! sm:p-0!">
            {d.dueToday.length === 0 ? (
              <div className="px-5 py-6 text-center text-[16px] text-ink-3">
                今天没有要交付的工件
              </div>
            ) : (
              <ul>
                {d.dueToday.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-start gap-3 border-b border-line px-5 py-4 last:border-0 sm:px-6"
                  >
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => navigate(`/cases/${c.id}`)}
                        className="block truncate text-left text-[17px] font-semibold hover:text-brand-700"
                      >
                        {c.customer.fullName}
                      </button>
                      <p className="tnum text-[15px] text-ink-3">
                        {c.caseNo} · {c.typeLabel}
                        {c.arch ? ` ${ARCH_ZH[c.arch]}` : ''}
                      </p>
                      <p className="tnum text-[15px] text-ink-2">
                        {rm(c.finalAmount)} · 已付 {rm(c.paidAmount)}
                        {c.balanceAmount > 0 && (
                          <span className="ml-2 font-semibold text-warn">
                            欠 {rm(c.balanceAmount)}
                          </span>
                        )}
                      </p>
                    </div>
                    {c.status !== 'DELIVERED' && (
                      <Button
                        size="sm"
                        loading={setCaseStatus.isPending}
                        onClick={() => setCaseStatus.mutate({ id: c.id, status: 'DELIVERED' })}
                      >
                        标记已交付
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <NewCaseDrawer
        open={caseFor !== null}
        onClose={() => setCaseFor(null)}
        presetCustomer={caseFor ?? undefined}
      />

      <AppointmentDrawer
        open={editAppt !== null}
        onClose={() => setEditAppt(null)}
        appointment={editAppt}
        isOwner={isOwner}
      />
    </div>
  )
}

function AppointmentRow({
  appt,
  busy,
  onArrived,
  onDone,
  onOpen,
  onStartCase,
  claiming,
  onEdit,
}: {
  appt: Appointment
  busy: boolean
  onArrived: () => void
  onDone: () => void
  onOpen: () => void
  onStartCase: () => void
  claiming: boolean
  onEdit: () => void
}) {
  const arrived = appt.status === 'ARRIVED'
  const waiting = appt.status === 'BOOKED' || appt.status === 'CONFIRMED'

  /**
   * 一条预约只给一个主行动，按状态变：
   *   还没来 → 已到店
   *   到店了、还没工单 → 开单（这是来店的目的）
   *   到店了、已有工单 → 完成
   */
  const action = waiting ? (
    <Button size="sm" onClick={onArrived} loading={busy}>
      已到店
    </Button>
  ) : arrived && !appt.case ? (
    <Button
      size="sm"
      variant="primary"
      icon={<Plus size={16} />}
      loading={claiming}
      onClick={onStartCase}
    >
      开单
    </Button>
  ) : arrived ? (
    <Button size="sm" variant="primary" onClick={onDone} loading={busy}>
      完成
    </Button>
  ) : null

  return (
    <li className="flex items-start gap-3 border-b border-line px-5 py-4 last:border-0 sm:px-6">
      {/* 点时间打开预约详情：改期、爽约、取消、删除都在那里 */}
      <button
        onClick={onEdit}
        aria-label="预约详情"
        className="w-16 shrink-0 rounded-md text-left transition-colors hover:bg-sunken"
      >
        <div className="tnum text-[19px] font-bold">{fmtTime(appt.startsAt)}</div>
        <Badge tone={apptStatusTone(appt.status)}>{APPT_STATUS_ZH[appt.status]}</Badge>
      </button>

      <div className="min-w-0 flex-1">
        <button
          onClick={onOpen}
          disabled={!appt.case}
          className="block truncate text-left text-[17px] font-semibold enabled:hover:text-brand-700 disabled:cursor-default"
        >
          {appt.displayName}
          {!appt.customer && (
            <span className="ml-2 align-middle text-[13px] font-normal text-ink-3">未建档</span>
          )}
        </button>

        {/* 目的比工单号有用 —— 老板要的是「他来做什么」 */}
        <p className="truncate text-[15px] text-ink-2">
          {appt.purpose ?? appt.caseStep?.title ?? '—'}
        </p>
        {appt.case && (
          <p className="tnum truncate text-[15px] text-ink-3">{appt.case.caseNo}</p>
        )}

        {appt.phoneE164 && (
          <a
            href={`tel:${appt.phoneE164}`}
            className="tnum mt-0.5 inline-flex items-center gap-1.5 text-[15px] font-medium text-brand-700 hover:underline"
          >
            <Phone size={15} />
            {appt.phoneE164}
          </a>
        )}
      </div>

      {action}
    </li>
  )
}

function AttentionRow({
  icon,
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  tone?: 'bad' | 'warn'
  onClick?: () => void
}) {
  return (
    <li>
      <button
        onClick={onClick}
        className="flex w-full items-center gap-3 border-b border-line px-5 py-3.5 text-left transition-colors last:border-0 hover:bg-sunken sm:px-6"
      >
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 text-[17px]">{label}</span>
        <span className="text-right">
          <span
            className={
              'tnum block text-[17px] font-bold ' +
              (tone === 'bad'
                ? 'text-bad'
                : tone === 'warn'
                  ? 'text-warn'
                  : '')
            }
          >
            {value}
          </span>
          {sub && <span className="block text-[14px] text-ink-3">{sub}</span>}
        </span>
      </button>
    </li>
  )
}

function TodaySkeleton() {
  return (
    <div className="mx-auto max-w-7xl">
      <Skeleton className="mb-5 h-9 w-72" />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Skeleton className="h-96" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-60" />
          <Skeleton className="h-40" />
        </div>
      </div>
    </div>
  )
}
